import type { UserLanguage } from '@xtreed/academy-shared';
import { useAuthStore } from '@/stores/auth';
import { usePWAStore } from '@/stores/pwa';
import { applyLanguage } from './index';
import { DEFAULT_LANGUAGE, isUserLanguage } from './languages';

interface LanguageSources {
  chosen: UserLanguage | null;
  tenant: UserLanguage | null;
  user: unknown;
  role?: unknown;
  isAuthenticated: boolean;
}

const STAFF_ROLES = ['SUPER_ADMIN', 'ADMIN', 'INSTRUCTOR'];

export function canChooseLanguage({
  tenant,
  role,
  isAuthenticated,
}: Pick<LanguageSources, 'tenant' | 'role' | 'isAuthenticated'>): boolean {
  if (!tenant) return true;
  return (
    isAuthenticated && typeof role === 'string' && STAFF_ROLES.includes(role)
  );
}

export function pickLanguage({
  chosen,
  tenant,
  user,
  role,
  isAuthenticated,
}: LanguageSources): UserLanguage {
  if (tenant && !canChooseLanguage({ tenant, role, isAuthenticated })) {
    return tenant;
  }
  if (chosen) return chosen;
  if (isAuthenticated && isUserLanguage(user)) return user;
  return tenant ?? DEFAULT_LANGUAGE;
}

export function resolveLanguage(): UserLanguage {
  const { language, tenantLanguage } = usePWAStore.getState();
  const { isAuthenticated, user } = useAuthStore.getState();
  return pickLanguage({
    chosen: language,
    tenant: tenantLanguage,
    user: user?.language,
    role: user?.role,
    isAuthenticated,
  });
}

export function startLanguageSync() {
  const apply = () => applyLanguage(resolveLanguage());
  apply();
  const unsubscribePWA = usePWAStore.subscribe(apply);
  const unsubscribeAuth = useAuthStore.subscribe(apply);
  return () => {
    unsubscribePWA();
    unsubscribeAuth();
  };
}
